import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { rememberReturnTo } from "./returnTo";
import { pathForStep } from "./session";

function PageLoading(): React.JSX.Element {
  return (
    <div className="nb-page-loading" aria-busy="true" aria-label="Loading">
      <span className="nb-spinner" style={{ color: "var(--ink-subtle)" }} />
    </div>
  );
}

/** Only lets a user through if they hold the given permission; otherwise sends them to the overview. */
export function RequirePermission({ permission, children }: { permission: string; children: ReactNode }): React.JSX.Element {
  const { user } = useAuth();

  if (!user) return <PageLoading />;
  if (!user.permissions.includes(permission)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

/** Only lets authenticated (next === "DONE") users through; otherwise redirects to the right auth step. */
export function RequireAuth({ children }: { children: ReactNode }): React.JSX.Element {
  const { state, loading } = useAuth();
  const location = useLocation();

  if (loading && !state) return <PageLoading />;

  if (!state?.authenticated || state.next !== "DONE") {
    const target = state ? pathForStep(state.next) : "/login";
    // Come back here once the login flow completes.
    rememberReturnTo(`${location.pathname}${location.search}`);
    return <Navigate to={target} replace state={{ from: location }} />;
  }

  return <>{children}</>;
}

/** Redirects an already-authenticated user away from the public auth pages. */
export function RedirectIfAuthenticated({ children }: { children: ReactNode }): React.JSX.Element {
  const { state, loading } = useAuth();

  if (loading && !state) return <PageLoading />;

  if (state?.authenticated && state.next === "DONE") {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
